import { useEffect, useState } from "react";
import { Lightbox } from "@/components/listing/Lightbox";
import { recordView } from "@/lib/recentlyViewed";
import { useParams, useNavigate } from "react-router-dom";
import { Columns2, Share2, Link2, Check, Heart } from "lucide-react";
import { Thumbs, Breadcrumb, Stats, ContactPanel, MediaTitle } from "@/components/listing/DesktopParts";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ListingCard } from "@/components/ListingCard";
import { Gallery } from "@/components/listing/Gallery";
import { DetailsTable } from "@/components/listing/DetailsTable";
import { SellerCard } from "@/components/listing/SellerCard";
import { OfficeCard } from "@/components/listing/OfficeCard";
import { BookingSection } from "@/components/listing/BookingSection";
import { ShareAsImage } from "@/components/listing/ShareAsImage";
import { ReportAd } from "@/components/listing/ReportAd";
import { BottomBar } from "@/components/listing/BottomBar";
import { api, imageUrl, formatApiErrorDetail } from "@/lib/api";
import { useCachedGet } from "@/lib/cache";
import { discountPct, adText, adPlace, servicePriceText, DESC_LABEL } from "@/lib/constants";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const priceText = (v, lang) => {
  const n = new Intl.NumberFormat("en-AE", { maximumFractionDigits: 2, useGrouping: false }).format(v || 0);
  return lang === "en" ? `AED ${n}` : `${n} درهم إماراتي`;
};

function useCompare(listingId, user) {
  const { data, setData } = useCachedGet("/compare", undefined, { enabled: !!user });
  const inList = !!data?.ids?.includes(listingId);
  const toggle = async () => {
    const { data: d } = inList ? await api.delete(`/compare/${listingId}`) : await api.post(`/compare/${listingId}`);
    setData((s) => ({ ...(s || {}), ids: d.ids }));
    return !inList;
  };
  return { inList, toggle };
}

function SimilarAds({ id, lang, grid }) {
  const { data } = useCachedGet(`/listings/${id}/similar`);
  if (!data?.length) return null;
  return (
    <section data-testid="similar-ads">
      <h2 className="font-headline font-bold text-lg mb-3">{lang === "en" ? "Similar ads" : "إعلانات مشابهة"}</h2>
      {grid
        ? <div className="grid grid-cols-2 gap-3">{data.slice(0, 6).map((l) => <ListingCard key={l.id} listing={l} />)}</div>
        : <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x">
          {data.map((l) => <div key={l.id} className="w-48 sm:w-56 shrink-0 snap-start"><ListingCard listing={l} /></div>)}
        </div>}
    </section>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const T = (ar, en) => (lang === "en" ? en : ar);
  const { user, toggleFavorite } = useAuth();
  const [idx, setIdx] = useState(0);
  const [viewer, setViewer] = useState(false);
  const [starting, setStarting] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data: listing, loading } = useCachedGet(`/listings/${id}`);
  const compare = useCompare(id, user);
  const desktop = useIsDesktop();

  useEffect(() => { setIdx(0); window.scrollTo(0, 0); }, [id]);
  useEffect(() => { if (listing?.id) recordView(listing.id); }, [listing?.id]);
  useEffect(() => {
    if (!listing?.category) return;
    const seen = JSON.parse(localStorage.getItem("bazaro_viewed_cats") || "[]").filter((c) => c !== listing.category);
    localStorage.setItem("bazaro_viewed_cats", JSON.stringify([listing.category, ...seen].slice(0, 5)));
  }, [listing?.category]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!listing) return <div className="min-h-screen flex flex-col"><Navbar /><div className="flex-1 flex items-center justify-center text-slate-500">{t("listing_notFound")}</div><Footer /></div>;

  const title = adText(listing, "title", lang);
  const price = servicePriceText(listing, lang, priceText) || priceText(listing.price, lang);
  const url = window.location.href;
  const mine = user?.user_id === listing.seller_id;
  const needLogin = () => { if (!user) { navigate("/login"); return true; } return false; };

  const onFav = async () => { if (needLogin()) return; try { await toggleFavorite(listing.id); } catch { toast.error(T("تعذّر تحديث المفضلة", "Couldn't update favourites")); } };
  const onShare = async () => {
    if (navigator.share) { try { await navigator.share({ title, text: `${title} - ${price}`, url }); } catch {} return; }
    onCopy();
  };
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success(T("تم نسخ الرابط", "Link copied")); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error(T("تعذّر النسخ", "Copy failed")); }
  };
  const onCompare = async () => {
    if (needLogin()) return;
    try { const added = await compare.toggle(); toast.success(added ? T("تمت الإضافة للمقارنة", "Added to compare") : T("تمت الإزالة من المقارنة", "Removed from compare"), added ? { action: { label: T("عرض المقارنة", "View"), onClick: () => navigate("/compare") } } : undefined); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const onChat = async () => {
    if (needLogin()) return;
    setStarting(true);
    try { const { data } = await api.post("/chat/conversations", { ad_id: listing.id }); navigate(`/messages/${data.conv_id}`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setStarting(false); }
  };
  const btn = "flex-1 h-12 rounded-2xl border border-[#EFEBE0] bg-white font-semibold flex items-center justify-center gap-2 hover:bg-[#1A5C3A]/5 transition-colors";
  const pct = discountPct(listing);
  const sideBtn = `${btn} text-sm px-2 gap-1.5 min-w-0 whitespace-nowrap`;
  const isFav = user?.favorites?.includes(listing.id);
  const priceBlock = ["domestic_workers", "home_services"].includes(listing.category) ? null : (
    <div className="flex items-end gap-3 flex-wrap">
      {pct > 0 && listing.original_price > 0 && <span className="text-slate-400 line-through text-lg whitespace-nowrap" data-testid="detail-original-price">{priceText(listing.original_price, lang)}</span>}
      <p className="text-[#1A5C3A] font-headline font-extrabold text-3xl sm:text-4xl lg:text-3xl" data-testid="detail-price">{price}</p>
      {pct > 0 && listing.original_price > 0 && <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap" data-testid="detail-discount-badge">{T(`خصم ${pct}%`, `${pct}% off`)}</span>}
    </div>
  );
  const compareBtn = (
    <button onClick={onCompare} className={`w-full h-12 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-colors ${compare.inList ? "bg-[#0F4D32] text-white" : "bg-[#E8EFEA] text-[#0F4D32] hover:bg-[#DDEAE3]"}`} data-testid="detail-add-compare">
      <Columns2 className="w-5 h-5" />{compare.inList ? T("مضاف للمقارنة", "In compare list") : T("أضف للمقارنة", "Add to compare")}
    </button>
  );
  const shareBtn = <button onClick={onShare} className={btn} data-testid="detail-share"><Share2 className="w-5 h-5 text-[#1A5C3A]" />{T("مشاركة", "Share")}</button>;
  const copyBtn = <button onClick={onCopy} className={btn} data-testid="detail-copy-link">{copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Link2 className="w-5 h-5 text-[#1A5C3A]" />}{T("نسخ الرابط", "Copy link")}</button>;
  const about = (
    <section className="bg-white rounded-3xl border border-[#EFEBE0] p-5">
      <h2 className="font-headline font-bold text-base mb-2">{T(...DESC_LABEL(listing.category))}</h2>
      <p className="text-slate-600 leading-relaxed whitespace-pre-line" data-testid="detail-description">{adText(listing, "description", lang)}</p>
    </section>
  );
  const shareImg = <ShareAsImage listing={listing} title={title} price={price} place={adPlace(listing, lang)} />;
  const booking = listing.category === "home_services" && <BookingSection listing={listing} lang={lang} mine={mine} />;
  const seller = ["domestic_workers", "home_services"].includes(listing.category)
    ? <OfficeCard listing={listing} />
    : <SellerCard sellerId={listing.seller_id} fallbackName={listing.seller_name} />;
  const h1 = <h1 className="font-headline text-2xl sm:text-3xl font-bold text-slate-900" data-testid="detail-title">{title}</h1>;
  const loc = <p className="sr-only" data-testid="detail-location">{adPlace(listing, lang)}</p>;
  const media = [...(listing.images || []).map((src) => ({ type: "image", src })), ...(listing.video ? [{ type: "video", src: listing.video, poster: listing.video_thumb }] : [])];
  const gallery = <>
    {(listing.images || []).length > 0 && <MediaTitle testid="detail-images-title">{T("صور الإعلان", "Ad photos")}</MediaTitle>}
    <Gallery media={media} idx={idx} setIdx={setIdx} alt={title} isFav={isFav} onFav={onFav} onShare={onShare} onOpen={(i) => { setIdx(i); setViewer(true); }} />
    {viewer && <Lightbox media={media} idx={idx} setIdx={setIdx} alt={title} onClose={() => setViewer(false)} />}
  </>;

  if (desktop) return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F3]">
      <Navbar />
      <main className="max-w-7xl 2xl:max-w-[1600px] mx-auto w-full px-6 py-8 flex-1" data-testid="listing-detail">
        <div className="grid grid-cols-[3fr_2fr] gap-8 items-start" data-testid="detail-desktop-layout">
          <div className="space-y-5 min-w-0" data-testid="detail-main-column">
            <div>{gallery}<Thumbs media={media} idx={idx} setIdx={setIdx} lang={lang} /></div>
            <div>
              <div className="space-y-2 mb-3">{h1}<Breadcrumb listing={listing} lang={lang} /><Stats listing={listing} lang={lang} />{loc}</div>
              <DetailsTable listing={listing} lang={lang} title={title} price={price} />
            </div>
            {booking}
            {about}
          </div>
          <aside className="space-y-4 min-w-0" data-testid="detail-side-column">
            <div className="bg-white rounded-3xl border border-[#EFEBE0] p-5 space-y-4" data-testid="detail-price-card">
              {priceBlock}
              {!mine && <ContactPanel phone={listing.phone} whatsapp={listing.whatsapp} onChat={onChat} starting={starting} listingId={listing.id} lang={lang} />}
              <div className="flex gap-2.5">
                <button onClick={onShare} className={sideBtn} data-testid="detail-share"><Share2 className="w-4 h-4 shrink-0 text-[#1A5C3A]" />{T("مشاركة", "Share")}</button>
                <button onClick={onFav} className={sideBtn} data-testid="detail-favorite-side"><Heart className={`w-4 h-4 shrink-0 ${isFav ? "fill-red-500 text-red-500" : "text-[#1A5C3A]"}`} />{T("تفضيل", "Favourite")}</button>
                {!mine && <ReportAd listingId={listing.id} className={`${sideBtn} text-red-600`} />}
              </div>
              {compareBtn}
            </div>
            {seller}
            <div className="flex gap-3">{copyBtn}</div>
            {shareImg}
            <SimilarAds id={listing.id} lang={lang} grid />
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F3]">
      <Navbar />
      <main className={`max-w-3xl mx-auto w-full sm:px-4 sm:py-6 flex-1 ${mine ? "pb-10" : "pb-28"}`} data-testid="listing-detail">
        <div className="px-4 sm:px-0 pt-4 sm:pt-0">{gallery}<Thumbs media={media} idx={idx} setIdx={setIdx} lang={lang} /></div>
        <div className="px-4 sm:px-0 space-y-4 mt-5">
          <div>{h1}{priceBlock && <div className="mt-2">{priceBlock}</div>}{loc}</div>
          <DetailsTable listing={listing} lang={lang} title={title} price={price} />
          {booking}
          {compareBtn}
          {about}
          {seller}
          <div className="flex gap-3">{shareBtn}{copyBtn}</div>
          {shareImg}
          {!mine && <ReportAd listingId={listing.id} />}
          <SimilarAds id={listing.id} lang={lang} />
        </div>
      </main>
      {!mine && <BottomBar phone={listing.phone} whatsapp={listing.whatsapp} onChat={onChat} starting={starting} listingId={listing.id} />}
    </div>
  );
}
